"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-80 bg-muted/50 animate-pulse rounded-md" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="overflow-hidden flex flex-col border-border shadow-sm hover:shadow-lg transition-shadow rounded-md bg-card">
          {post.featuredImage && (
            <AspectRatio ratio={16 / 9}>
              <img
                src={post.featuredImage}
                alt={post.title}
                className="object-cover w-full h-full"
              />
            </AspectRatio>
          )}
          <CardHeader className="pb-3">
            <CardTitle className="line-clamp-2 text-xl font-default text-foreground">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <div
              className="text-muted-foreground line-clamp-3 text-sm font-sans"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"}>
              <Button variant="link" className="px-0 text-primary font-semibold">
                Read Analysis
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId }: { currentPostId: string }) {
  
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 3,
    post__not: [currentPostId],
  });

  return (
    <PostsProvider wp_query={wp_query}>
      <RelatedPostsConsumer />
    </PostsProvider>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-16 animate-pulse">
        <div className="h-12 bg-muted/50 rounded-md w-3/4 mb-6" />
        <div className="h-6 bg-muted/50 rounded-md w-1/4 mb-12" />
        <div className="h-[400px] bg-muted/50 rounded-md mb-12" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/50 rounded w-full" />
          <div className="h-4 bg-muted/50 rounded w-full" />
          <div className="h-4 bg-muted/50 rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-24 text-center">
        <h1 className="text-3xl font-bold font-default text-foreground mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-8">The financial guide you are looking for is currently unavailable.</p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground hover:opacity-90 rounded-md">Return to FINCALC</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 max-w-4xl py-16 md:py-24">
      {}
      <header className="mb-10 text-center md:text-left">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-4">
            <span className="text-sm font-bold text-primary uppercase tracking-widest">
              {post.categories[0].name}
            </span>
          </div>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight mb-6 tracking-tight">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-muted-foreground text-sm font-sans">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={post.author.avatar?.["24"]} alt={post.author.name} />
                <AvatarFallback className="bg-secondary text-secondary-foreground">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-medium">{post.author.name}</span>
            </div>
          )}
          {post.author && post.date && <span>•</span>}
          {post.date && (
            <time dateTime={post.date} className="font-medium">
              {new Date(post.date).toLocaleDateString("en-CA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-16 rounded-md overflow-hidden shadow-xl border border-border">
          <AspectRatio ratio={21 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </figure>
      )}

      {}
      <div className="max-w-3xl mx-auto">
        <div
          className="text-foreground leading-relaxed text-lg font-sans [&>p]:mb-6 [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:mb-4 [&>h2]:mt-10 [&>h2]:text-foreground [&>h2]:tracking-tight [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mb-3 [&>h3]:mt-8 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:italic [&>blockquote]:mb-6 [&>blockquote]:bg-muted [&>blockquote]:rounded-md [&>img]:rounded-md [&>img]:my-8 [&>img]:w-full [&>img]:h-auto [&>img]:shadow-md [&>a]:text-primary [&>a]:font-semibold [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </div>

      <Separator className="my-16 max-w-3xl mx-auto bg-border" />

      {}
      {post.author && (
        <div className="max-w-3xl mx-auto bg-card border border-border rounded-md p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 shadow-md">
          <Avatar className="h-24 w-24 border-4 border-background shadow-sm">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl bg-primary/10 text-primary font-default">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left flex-1">
            <h3 className="text-2xl font-bold font-default text-foreground mb-2">
              {post.author.name}
            </h3>
            <p className="text-muted-foreground mb-4 font-sans">
              Financial Analyst at FINCALC. Specializing in Ontario tax regulations, HST compliance, and Canadian retirement planning strategies to help you maximize your wealth.
            </p>
            {post.author.link && (
              <Link to={post.author.link}>
                <Button variant="outline" size="sm" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                  View all insights
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {}
      <div className="mt-24 pt-12 border-t border-border">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-3xl font-bold font-default text-foreground tracking-tight">
            Financial Resources
          </h2>
          <Link to="/HstCalculatorPage">
            <Button variant="ghost" className="hidden sm:flex text-primary font-semibold hover:bg-accent">
              Explore all tools
            </Button>
          </Link>
        </div>
        <RelatedPostsWrapper currentPostId={post.id} />
        <div className="mt-8 text-center sm:hidden">
          <Link to="/HstCalculatorPage">
            <Button variant="outline" className="w-full rounded-md border-primary text-primary">
              Explore all tools
            </Button>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="240598" id="single-post-template-section" className="bg-background min-h-screen text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}